import { Node, mergeAttributes, ReactNodeViewRenderer } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import type { ComponentType } from 'react';

interface AtomBlockOptions {
    /** TipTap node name, e.g. `imageBlock` */
    name: string;
    /** `data-type` marker in the serialised HTML, e.g. `image-block` */
    tag: string;
    /** Default attrs == default block data */
    attributes: Record<string, unknown>;
    view: ComponentType<NodeViewProps>;
}

/**
 * Factory for the atom block nodes whose attrs are exactly the block data and
 * whose editing UI is a React node view (the Scilora pattern, one file per block
 * collapsed into one helper). Keeps the 25+ extensions down to a table.
 */
export function createAtomBlock({ name, tag, attributes, view }: AtomBlockOptions) {
    return Node.create({
        name,
        group: 'block',
        atom: true,
        draggable: true,

        addAttributes() {
            const attrs: Record<string, { default: unknown }> = {
                blockId: { default: null },
                blockMeta: { default: null },
            };
            for (const [key, value] of Object.entries(attributes)) {
                attrs[key] = { default: value };
            }
            return attrs;
        },

        parseHTML() {
            return [{ tag: `div[data-type="${tag}"]` }];
        },

        renderHTML({ HTMLAttributes }) {
            return ['div', mergeAttributes(HTMLAttributes, { 'data-type': tag })];
        },

        addNodeView() {
            // #520: keystrokes inside the block's own form controls (question
            // text, alt text, choices …) must not reach ProseMirror — with the
            // atom node selected it treated a typed space as text input and
            // spawned a new paragraph next to the block, and swallowed letters.
            return ReactNodeViewRenderer(view as Parameters<typeof ReactNodeViewRenderer>[0], { stopEvent: ({ event }) => isInsideFormControl(event.target) });
        },
    });
}

/** True for events that originate in an input, textarea, select, button or nested editable inside a node view. */
export function isInsideFormControl(target: EventTarget | null): boolean {
    const el = target as HTMLElement | null;
    if (!el || typeof el.closest !== 'function') return false;
    return el.closest('input, textarea, select, button, [role="button"], [role="combobox"], [role="listbox"], [role="option"], .MuiPopover-root, [contenteditable="true"]:not(.oa-block-editor-content)') !== null;
}
